import { Asset, Token } from "../../types";
import {
    ApiQuery,
    ApiQueryEndpoints,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
} from "~/modules/filters/components/ApiSearchLayout";
import { Transaction, TransactionColumns } from "~/modules/transactions/types";
import { ASSET_INDIVIDUAL_TRANSACTION_TABLE_COLUMNS } from "~/modules/transactions/components/TransactionTable/columns";
import { DataGrid, DataGridRow, RowSizes } from "~/components/DataGrid";
import { isStablecoin } from "~/utils/isAssetClass";
import { useApiQuery } from "~/modules/filters/components/useApiQuery";
import { TabSelectItem, VerticalTabSelect } from "~/components/TabSelect";
import { IconTypes } from "~/components/Icon/types";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessageDollar } from "@fortawesome/pro-regular-svg-icons";
import { RWA_DATA_EMAIL_ADDRESS } from "~/consts";
import { Anchor } from "~/components/Anchor";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { ImageLoader } from "~/components/ImageLoader";
import { ButtonVariants, getButtonClassName } from "~/components/Button";
import { useUser } from "~/modules/user/hooks/useUser";
import { TokenHoldersCard } from "~/modules/token_holders/components/AssetTokenHolders/TokenHoldersCard";
import * as Analytics from "../../analytics";
import { ConfidentialNetworkBanner } from "~/components/ConfidentialNetworkNotice";
import { isConfidentialNetwork } from "~/modules/token_holders/components/AssetTokenHolders/TokenHoldersEmptyState";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { useRegistrationModal } from "~/modules/auth/hooks/useRegistrationModal";

// // // //

/**
 * Renders a login CTA for Asset Transactions
 */
export function TransactionsLoginCTA(props: { asset: Asset }) {
    const { asset } = props;
    const { open: openRegistrationModal } = useRegistrationModal();

    return (
        <div className="relative isolate overflow-hidden px-6 pt-16 sm:px-16 md:pt-24 lg:flex lg:gap-x-20 lg:px-24 lg:pt-0">
            <div className="mx-auto max-w-md text-center lg:mx-0 lg:flex-auto lg:py-32 lg:text-left">
                <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                    View detailed
                    <br />
                    transaction records
                </h2>
                <p className="mt-6 text-lg leading-8 text-secondary-foreground">
                    Sign up for a free account to get access to detailed
                    transaction records between investor addresses on RWA across
                    blockchain networks.
                </p>
                <div className="mt-10 flex items-center justify-center gap-x-6 lg:justify-start">
                    <button
                        onClick={() => {
                            openRegistrationModal({
                                label: AssetUserEvents.TransactionsLoginCTA,
                                properties: {
                                    asset_id: String(asset.id),
                                    asset_ticker: asset.ticker ?? "",
                                },
                            });
                        }}
                        className={getButtonClassName({
                            size: "md",
                            variant: ButtonVariants.primaryLink,
                            className: "gap-2",
                        })}
                    >
                        Log in to Unlock
                        <span aria-hidden="true">→</span>
                    </button>
                </div>
            </div>
            <div className="relative mt-16 h-80 lg:mt-8">
                <ImageLoader
                    className="absolute left-0 top-0 w-228 max-w-none rounded-lg bg-white/5 ring-1 ring-white/10 shadow-lg border"
                    src="/transactions-product-screenshot.png"
                    alt="Holders Product Screenshot"
                />
            </div>
        </div>
    );
}

// // // //

/**
 * Renders an empty state for Asset Transactions
 */
function TransactionsEmptyState() {
    return (
        <div
            className="relative w-full h-full flex flex-col justify-center items-center px-12 py-24 text-center text-slate-400"
            style={{ minHeight: "612px" }}
        >
            <FontAwesomeIcon
                className="mx-auto h-12 w-12"
                icon={faMessageDollar}
            />
            <p className="mt-4 block text-lg font-light">
                No transactions data currently available
            </p>
            <p className="mt-4 block text-base font-light">
                Please check back later
            </p>

            <div className="flex items-center mt-2">
                <div className="grow h-px bg-slate-400 w-10" />
                <span className="px-4 text-sm font-light text-slate-400">
                    or
                </span>
                <div className="grow h-px bg-slate-400 w-10" />
            </div>

            <p className="mt-2 block text-base font-light">
                email us at{" "}
                <Anchor
                    href={`mailto:${RWA_DATA_EMAIL_ADDRESS}`}
                    analyticsLabel={`Asset Transactions Empty State - email ${RWA_DATA_EMAIL_ADDRESS}`}
                    variant="default"
                    className="underline underline-offset-4"
                >
                    {RWA_DATA_EMAIL_ADDRESS}
                </Anchor>{" "}
                to report missing or incorrect data
            </p>
        </div>
    );
}

// // // //

function getAssetTransactionsQuery(props: {
    asset: Asset;
    token?: Token | null;
}): ApiQuery {
    const { asset, token } = props;

    const query: ApiQuery = {
        filter: {
            operator: FilterOperators.And,
            filters: [
                {
                    operator: FilterOperators.Equals,
                    field: TransactionColumns.assetID,
                    value: asset.id,
                },
            ],
        },
        sort: {
            direction: SortDirections.desc,
            field: TransactionColumns.timestamp,
        },
        pagination: {
            page: 1,
            perPage: 15,
        },
    };

    if (!token || token.address === null) {
        return query;
    }

    const tokenQuery: ApiQuery = {
        ...query,
        filter: {
            operator: FilterOperators.Or,
            filters: [
                {
                    operator: FilterOperators.Equals,
                    field: TransactionColumns.tokenID,
                    value: token.id,
                },
            ],
        },
    };

    return tokenQuery;
}

// // // //

function TransactionList(props: { asset: Asset; token: Token | null }) {
    const { asset, token } = props;

    const ASSET_TRANSACTIONS_QUERY = getAssetTransactionsQuery({
        asset,
        token,
    });

    return (
        <ApiQueryStateManager defaultFilters={ASSET_TRANSACTIONS_QUERY}>
            {({ appliedQuery, internalQuery }) => (
                <ApiQueryFetcher<Transaction>
                    appliedQuery={appliedQuery.query}
                    internalQuery={internalQuery.query}
                    endpoint={ApiQueryEndpoints.TransactionsV4}
                    initialState={{
                        results: [],
                        pagination: {
                            page: 0,
                            perPage: 0,
                            pageCount: 0,
                            resultCount: 0,
                        },
                        sort: {
                            direction: SortDirections.desc,
                            field: TransactionColumns.timestamp,
                        },
                    }}
                >
                    {(state) => {
                        // Bypass ApiSearchLayout loading state
                        if (
                            state.appliedQueryReq.data.results.length === 0 &&
                            state.appliedQueryReq.loading
                        ) {
                            return (
                                <div className="relative w-full h-full flex flex-col justify-center items-center px-12 py-36 text-center text-slate-400">
                                    <LoadingSpinner size={10} />
                                </div>
                            );
                        }

                        // Bypass ApiSearchLayout empty state
                        if (state.appliedQueryReq.data.results.length === 0) {
                            return <TransactionsEmptyState />;
                        }

                        return (
                            <DataGrid
                                key={token?.id ?? "all-tokens"}
                                query={appliedQuery.query}
                                columns={
                                    ASSET_INDIVIDUAL_TRANSACTION_TABLE_COLUMNS
                                }
                                rows={[
                                    // Ensure unique ID for each row in results
                                    ...state.appliedQueryReq.data.results.map(
                                        (r) => ({
                                            ...r,
                                            id: `${r.id}-${r.to_address}-${r.from_address}-${r.transaction_hash}-${r.amount}`,
                                        }),
                                    ),
                                ]}
                                rowKey="id"
                                bordered
                                size={RowSizes.small}
                                loading={state.appliedQueryReq.loading}
                                fixedHeader
                                analyticsTableName="transactions"
                                onChangeQuery={appliedQuery.update}
                                flush
                                includeDummyRows={
                                    state.appliedQueryReq.data.pagination
                                        .pageCount > 1
                                }
                                paginationState={
                                    state.appliedQueryReq.data.pagination
                                }
                                enablePagerPagination
                            >
                                {({ row, rowIndex, scrollLeft }) => (
                                    <DataGridRow
                                        key={row.id}
                                        bordered
                                        row={row}
                                        scrollLeft={scrollLeft}
                                        columns={
                                            ASSET_INDIVIDUAL_TRANSACTION_TABLE_COLUMNS
                                        }
                                        size={RowSizes.small}
                                        striped={rowIndex % 2 === 1}
                                    />
                                )}
                            </DataGrid>
                        );
                    }}
                </ApiQueryFetcher>
            )}
        </ApiQueryStateManager>
    );
}

// // // //

function AssetTransactionPreFetcher(props: {
    asset: Asset;
    children: (childProps: {
        loading: boolean;
        hasData: boolean;
    }) => React.ReactNode;
}) {
    const { asset, children } = props;

    const prefetchQuery = useApiQuery({
        endpoint: ApiQueryEndpoints.TransactionsV4,
        query: getAssetTransactionsQuery({ asset }),
    });

    return (
        <>
            {children({
                loading: prefetchQuery.isLoading,
                hasData: !!(
                    prefetchQuery.data &&
                    prefetchQuery.data.pagination.resultCount > 0
                ),
            })}
        </>
    );
}

// // // //

type TokenHoldersTab = TabSelectItem<{
    token: Token | null;
}>;

function getTabs(props: { asset: Asset }): Array<TokenHoldersTab> {
    const { asset } = props;
    const validTokens = asset.tokens;

    // Build items for TabSelect
    const tabs: Array<TokenHoldersTab> = validTokens.map((t) => {
        return {
            key: String(t.id),
            content: {
                label: t.network?.name ?? t.name,
                icon: t.network?.icon_url,
            },
            data: {
                token: t,
            },
        };
    });

    // Add "All Tokens" tab if there is more than one tab
    if (tabs.length > 1) {
        const ALL_TOKENS_TAB: TokenHoldersTab = {
            key: "all",
            content: {
                label: "All Networks",
                icon: IconTypes.CircleNodes,
            },
            data: {
                token: null,
            },
        };

        tabs.unshift(ALL_TOKENS_TAB);
    }

    return tabs;
}

// // // //

// ENHANCEMENT - this card pattern should be built into a dedicated component (this is copy/pasted in many places)
export function AssetTransactions(props: { asset: Asset }) {
    const { asset } = props;

    const { isLoggedIn } = useUser();

    // Don't render transactions for stablecoins
    if (isStablecoin({ asset })) {
        return null;
    }

    return (
        <AssetTransactionPreFetcher asset={asset}>
            {({ loading, hasData }) => {
                if (loading) {
                    return null;
                }

                if (!hasData) {
                    return null;
                }

                // If user isn't logged in -> render CTA instead
                if (!isLoggedIn) {
                    return (
                        <TokenHoldersCard title="Transactions" token_count={0}>
                            <TransactionsLoginCTA asset={asset} />
                        </TokenHoldersCard>
                    );
                }

                const tabs = getTabs({ asset });

                if (tabs.length === 0) {
                    return null;
                }

                return (
                    <TokenHoldersCard
                        title="Transactions"
                        token_count={tabs.length}
                    >
                        <VerticalTabSelect
                            tabs={tabs}
                            name="asset-transactions-tabs"
                            onTabChange={({ prev, next }) => {
                                // Track tab change in analytics
                                Analytics.trackTransactionsTabChange({
                                    prev: prev.content.label,
                                    next: next.content.label,
                                    asset_id: asset.id,
                                    asset_ticker: asset.ticker ?? "",
                                });
                            }}
                        >
                            {(tab) => {
                                if (!tab) {
                                    return null;
                                }

                                const network = tab.data.token?.network;
                                const showConfidentialBanner =
                                    isConfidentialNetwork(network?.slug);

                                return (
                                    <>
                                        {showConfidentialBanner && (
                                            <ConfidentialNetworkBanner
                                                networkName={
                                                    network?.name ??
                                                    "this network"
                                                }
                                            />
                                        )}
                                        <TransactionList
                                            asset={asset}
                                            token={tab.data.token}
                                        />
                                    </>
                                );
                            }}
                        </VerticalTabSelect>
                    </TokenHoldersCard>
                );
            }}
        </AssetTransactionPreFetcher>
    );
}
